import React, { useEffect, useState } from "react";
import { api, API } from "../api";
import useIdleLogout from "../hooks/useIdleLogout";
import IdleLogoutWarning from "../components/IdleLogoutWarning";
import { AnalyticsEaKey } from "../components/AnalyticsEaKey";
import { AnalyticsStrategyReport } from "../components/AnalyticsStrategyReport";
import { AnalyticsAccountSelect, AnalyticsAccountsTable } from "../components/AnalyticsAccounts";

/**
 * Admin — Analytics post-mortem des trades.
 * Ingestion via POST /api/admin/analytics/ingest depuis le collecteur MT5.
 * Génération de rapport à la demande.
 */
export default function AdminAnalytics() {
  const token = localStorage.getItem("dtsp_admin_token") || "";
  const headers = { headers: { "x-admin-token": token } };

  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [reports, setReports] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sinceDays, setSinceDays] = useState(30);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rangeMode, setRangeMode] = useState("preset"); // "preset" | "custom"
  const [account, setAccount] = useState("");
  const [err, setErr] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [schedule, setSchedule] = useState({ enabled: false });
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedMsg, setSchedMsg] = useState("");

  // Auto-logout après 60 minutes d'inactivité
  const doLogout = async () => {
    try { await api.post("/admin/logout", null, headers); } catch {}
    localStorage.removeItem("dtsp_admin_token");
    try { localStorage.removeItem("dtsp_admin_last_activity"); } catch {}
    window.location.href = "/admin";
  };
  const { showWarning, remaining, stayActive } = useIdleLogout({
    idleMs: 60 * 60 * 1000,
    warnMs: 60 * 1000,
    onLogout: doLogout,
  });

  useEffect(() => {
    if (!token) return;
    api.get("/admin/analytics/stats", headers).then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/analytics/health", headers).then(({ data }) => setHealth(data)).catch(() => {});
    api.get("/admin/analytics/reports", headers).then(({ data }) => setReports(data.reports || [])).catch(() => {});
    api.get("/admin/analytics/email-schedule", headers).then(({ data }) => setSchedule(data || { enabled: false })).catch(() => {});
  }, [token]); // eslint-disable-line

  const generate = async () => {
    setBusy(true); setErr("");
    try {
      const params = rangeMode === "custom" && (dateFrom || dateTo)
        ? `date_from=${dateFrom}&date_to=${dateTo}`
        : `since_days=${sinceDays}`;
      const { data } = await api.post(`/admin/analytics/generate-report?${params}&account=${encodeURIComponent(account)}`, {}, headers);
      setCurrent(data);
      const { data: list } = await api.get("/admin/analytics/reports", headers);
      setReports(list.reports || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const loadReport = async (id) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/admin/analytics/reports/${id}`, headers);
      setCurrent(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    if (!current) return;
    const rows = [
      ["Généré le", current.generated_at],
      ["Période", `${current.period?.from || "?"} → ${current.period?.to || "?"}`],
      ["Total trades", current.total_trades],
      ["Winrate %", current.winrate],
      ["P&L net $", current.net_pnl],
      [""],
      ["Patterns identifiés"],
      ["Dimension", "Clé", "Trades", "Wins", "Winrate %", "Deep losses", "P&L net"],
      ...(current.patterns || []).map(p => [p.dimension, p.key, p.trades, p.wins, p.winrate, p.deep_losses, p.net_pnl]),
      [""],
      ["Recommandations"],
      ...(current.recommendations || []).map(r => [r.severity, r.text]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `analytics_${current.id?.slice(0,8) || "report"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    if (!current?.id) return;
    setPdfBusy(true); setErr("");
    try {
      const res = await fetch(`${API}/admin/analytics/reports/${current.id}/pdf`, {
        headers: { "x-admin-token": token },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `analytics_${current.id.slice(0,8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e.message || "Erreur export PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="page-admin-analytics">
      <a
        href="/admin"
        className="inline-flex items-center gap-1.5 mb-4 text-xs text-zinc-400 hover:text-cyan-300 underline underline-offset-4 decoration-cyan-400/40"
        data-testid="analytics-back-btn"
      >
        ← Retour au dashboard admin
      </a>
      <h1 className="text-3xl font-bold tracking-tight mb-2">📊 Analytics — Post-mortem des trades</h1>
      <p className="text-sm text-zinc-400 mb-6">
        Analyse déterministe (français, 100% gratuit) des trades ingérés depuis MT5.
        Un batch quotidien à 00h00 UTC — ou à la demande via le bouton ci-dessous.
      </p>

      {/* Bandeau santé collecteur MT5 */}
      {health && health.status !== "ok" && (
        <div
          className={`mb-4 p-4 rounded-xl border ${
            health.status === "no_data" || health.status === "seed_only"
              ? "border-amber-400/40 bg-amber-500/10"
              : health.status === "stale"
              ? "border-red-400/40 bg-red-500/10"
              : "border-yellow-400/40 bg-yellow-500/10"
          }`}
          data-testid="analytics-health-banner"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-semibold">
                {health.status === "no_data" && "⚠️ Aucun trade jamais reçu par le backend"}
                {health.status === "seed_only" && "⚠️ Uniquement les données de démo — le collecteur MT5 n'envoie rien"}
                {health.status === "stale" && "🔴 Le collecteur MT5 est muet"}
                {health.status === "warning" && "🟡 Ingestion ralentie"}
              </div>
              <div className="text-xs text-zinc-300 mt-1">
                {health.status_label}
                {health.last_ingested_at && (
                  <> — dernier ingest : <span className="font-mono">{health.last_ingested_at.slice(0, 16).replace("T", " ")} UTC</span></>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowChecklist(v => !v)}
              className="btn-ghost px-3 py-1.5 rounded-lg text-xs whitespace-nowrap"
              data-testid="analytics-checklist-toggle"
            >
              {showChecklist ? "Masquer la marche à suivre" : "Voir la marche à suivre"}
            </button>
          </div>

          {showChecklist && (
            <div className="mt-4 pt-4 border-t border-white/10 text-xs space-y-3" data-testid="analytics-checklist">
              <div className="font-semibold text-sm">Configuration du collecteur MT5 (5 min)</div>
              <ol className="list-decimal list-inside space-y-2 text-zinc-200">
                <li>
                  <strong>Whitelister l'URL</strong> dans MT5 : <span className="font-mono">Outils → Options → Expert Advisors → Autoriser WebRequest pour les URLs suivantes</span> → ajouter :
                  <div className="mt-1 p-2 rounded bg-black/40 font-mono text-cyan-300 select-all">https://drtradebots.com</div>
                </li>
                <li>
                  <strong>Copier la Clé EA</strong> (encadré ci-dessous) et la coller dans l'input <span className="font-mono">Analytics_API_Token</span> de l'EA.
                </li>
                <li>
                  Dans les inputs de l'EA (F7 → onglet <em>Inputs</em>), section <strong>31. Analytics</strong> :
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                    <li><span className="font-mono">Analytics_Enable</span> → <strong className="text-emerald-300">true</strong></li>
                    <li><span className="font-mono">Analytics_API_Token</span> → coller la Clé EA</li>
                    <li><span className="font-mono">Analytics_Rebuild_History_On_Init</span> → <strong className="text-emerald-300">true</strong> (renvoie les trades des 90 derniers jours)</li>
                  </ul>
                </li>
                <li>
                  Retirer / replacer l'EA sur le graphique. Dans le <em>Journal</em> MT5 vous verrez :
                  <div className="mt-1 p-2 rounded bg-black/40 font-mono text-[10px] text-emerald-300">📊 [Analytics] Historique 90 j : N trades envoyés</div>
                  <div className="mt-1 text-zinc-400">En cas d'échec, le Journal affiche la cause exacte (URL non autorisée, clé invalide…).</div>
                </li>
                <li>Rafraîchir cette page — les trades apparaîtront dans les stats.</li>
              </ol>
            </div>
          )}
        </div>
      )}

      {token && <AnalyticsEaKey headers={headers} />}

      {/* Stats globales */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          <div className="glass p-4 rounded-xl">
            <div className="text-xs text-zinc-400">Trades ingérés</div>
            <div className="text-2xl font-bold" data-testid="analytics-stat-total">{stats.total_trades}</div>
          </div>
          <div className="glass p-4 rounded-xl">
            <div className="text-xs text-zinc-400">Wins / Losses / DeepLoss</div>
            <div className="text-lg font-mono">
              <span className="text-emerald-400">{stats.verdicts?.WIN || 0}</span> ·{" "}
              <span className="text-amber-400">{stats.verdicts?.LOSS || 0}</span> ·{" "}
              <span className="text-red-400">{stats.verdicts?.DEEP_LOSS || 0}</span>
            </div>
          </div>
          <div className="glass p-4 rounded-xl">
            <div className="text-xs text-zinc-400">Premier trade</div>
            <div className="text-xs font-mono">{stats.first_trade?.slice(0,10) || "—"}</div>
          </div>
          <div className="glass p-4 rounded-xl">
            <div className="text-xs text-zinc-400">Dernier ingest</div>
            <div className="text-xs font-mono" data-testid="analytics-last-ingest">
              {health?.minutes_since_last_ingest == null
                ? "—"
                : health.minutes_since_last_ingest < 60
                ? `il y a ${health.minutes_since_last_ingest} min`
                : health.minutes_since_last_ingest < 60 * 24
                ? `il y a ${Math.floor(health.minutes_since_last_ingest / 60)}h`
                : `il y a ${Math.floor(health.minutes_since_last_ingest / (60 * 24))}j`}
            </div>
          </div>
          <div className="glass p-4 rounded-xl">
            <div className="text-xs text-zinc-400">Dernier trade</div>
            <div className="text-xs font-mono">{stats.last_trade?.slice(0,10) || "—"}</div>
          </div>
        </div>
      )}

      {/* Génération */}
      <div className="glass p-4 rounded-xl mb-6 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-3 mb-2">
            <label className="text-xs text-zinc-400">Fenêtre d'analyse</label>
            <div className="flex gap-1 text-[11px]">
              <button
                onClick={() => setRangeMode("preset")}
                className={`px-2 py-1 rounded ${rangeMode === "preset" ? "bg-cyan-600/30 text-cyan-200" : "text-zinc-400 hover:text-zinc-200"}`}
                data-testid="analytics-range-preset">Préréglage</button>
              <button
                onClick={() => setRangeMode("custom")}
                className={`px-2 py-1 rounded ${rangeMode === "custom" ? "bg-cyan-600/30 text-cyan-200" : "text-zinc-400 hover:text-zinc-200"}`}
                data-testid="analytics-range-custom">Dates personnalisées</button>
            </div>
          </div>
          {rangeMode === "preset" ? (
            <select value={sinceDays} onChange={e => setSinceDays(Number(e.target.value))}
                    className="px-3 py-2 rounded-lg text-sm w-full" data-testid="analytics-since-select">
              <option value={7}>7 derniers jours</option>
              <option value={30}>30 derniers jours</option>
              <option value={90}>90 derniers jours</option>
              <option value={180}>180 derniers jours</option>
              <option value={365}>1 an</option>
            </select>
          ) : (
            <div className="flex items-center gap-2">
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                     className="px-3 py-2 rounded-lg text-sm bg-black/40 border border-white/10"
                     data-testid="analytics-date-from" placeholder="Date début"/>
              <span className="text-zinc-500 text-xs">→</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                     className="px-3 py-2 rounded-lg text-sm bg-black/40 border border-white/10"
                     data-testid="analytics-date-to" placeholder="Date fin"/>
            </div>
          )}
        </div>
        <div className="min-w-[240px]">
          <label className="text-xs text-zinc-400 block mb-2">Compte MT5</label>
          <AnalyticsAccountSelect headers={headers} value={account} onChange={setAccount}
                                  testid="analytics-account-select" />
        </div>
        <button onClick={generate} disabled={busy}
                className="btn-primary px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                data-testid="analytics-generate-btn">
          {busy ? "Analyse en cours…" : "🔬 Générer un rapport maintenant"}
        </button>
        {current && (
          <button onClick={exportCsv}
                  className="btn-ghost px-4 py-2 rounded-lg text-sm"
                  data-testid="analytics-export-btn">
            📥 Exporter CSV
          </button>
        )}
        {current && (
          <button onClick={exportPdf} disabled={pdfBusy}
                  className="btn-ghost px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                  data-testid="analytics-export-pdf-btn">
            {pdfBusy ? "Génération PDF…" : "📄 Exporter PDF"}
          </button>
        )}
      </div>
      {err && <div className="text-red-400 text-sm mb-4">{err}</div>}

      {/* Envoi automatique par email */}
      <div className="glass p-4 rounded-xl mb-6" data-testid="analytics-email-schedule">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-sm font-semibold">📧 Envoi automatique du rapport par email</div>
            <div className="text-xs text-zinc-400 mt-0.5">
              Recevez le PDF quotidiennement, hebdomadairement ou mensuellement.
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-xs">
            <input type="checkbox"
                   checked={!!schedule?.enabled}
                   onChange={e => setSchedule({ ...(schedule || {}), enabled: e.target.checked, frequency: schedule?.frequency || "weekly", hour_utc: schedule?.hour_utc ?? 8, window_days: schedule?.window_days ?? 30 })}
                   data-testid="analytics-email-enabled"/>
            <span>{schedule?.enabled ? "Activé" : "Désactivé"}</span>
          </label>
        </div>
        {schedule?.enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-[11px] text-zinc-400 block mb-1">Email destinataire</label>
              <input type="email" value={schedule?.email || ""}
                     onChange={e => setSchedule({ ...schedule, email: e.target.value })}
                     placeholder="vous@exemple.com"
                     className="w-full px-3 py-2 rounded-lg text-sm bg-black/40 border border-white/10"
                     data-testid="analytics-email-recipient"/>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 block mb-1">Fréquence</label>
              <select value={schedule?.frequency || "weekly"}
                      onChange={e => setSchedule({ ...schedule, frequency: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      data-testid="analytics-email-frequency">
                <option value="daily">Quotidien</option>
                <option value="weekly">Hebdomadaire</option>
                <option value="monthly">Mensuel</option>
              </select>
            </div>
            {schedule?.frequency === "weekly" && (
              <div>
                <label className="text-[11px] text-zinc-400 block mb-1">Jour de la semaine</label>
                <select value={schedule?.day_of_week ?? 1}
                        onChange={e => setSchedule({ ...schedule, day_of_week: Number(e.target.value) })}
                        className="w-full px-3 py-2 rounded-lg text-sm"
                        data-testid="analytics-email-dow">
                  <option value={0}>Lundi</option>
                  <option value={1}>Mardi</option>
                  <option value={2}>Mercredi</option>
                  <option value={3}>Jeudi</option>
                  <option value={4}>Vendredi</option>
                  <option value={5}>Samedi</option>
                  <option value={6}>Dimanche</option>
                </select>
              </div>
            )}
            {schedule?.frequency === "monthly" && (
              <div>
                <label className="text-[11px] text-zinc-400 block mb-1">Jour du mois (1-28)</label>
                <input type="number" min={1} max={28}
                       value={schedule?.day_of_month ?? 1}
                       onChange={e => setSchedule({ ...schedule, day_of_month: Math.max(1, Math.min(28, Number(e.target.value))) })}
                       className="w-full px-3 py-2 rounded-lg text-sm bg-black/40 border border-white/10"
                       data-testid="analytics-email-dom"/>
              </div>
            )}
            <div>
              <label className="text-[11px] text-zinc-400 block mb-1">Heure (UTC)</label>
              <select value={schedule?.hour_utc ?? 8}
                      onChange={e => setSchedule({ ...schedule, hour_utc: Number(e.target.value) })}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      data-testid="analytics-email-hour">
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, "0")}h00 UTC</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 block mb-1">Compte MT5</label>
              <AnalyticsAccountSelect headers={headers} value={schedule?.account || ""}
                                      onChange={(v) => setSchedule({ ...schedule, account: v })}
                                      testid="analytics-email-account" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 block mb-1">Fenêtre analysée</label>
              <select value={schedule?.window_days ?? 30}
                      onChange={e => setSchedule({ ...schedule, window_days: Number(e.target.value) })}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      data-testid="analytics-email-window">
                <option value={7}>7 derniers jours</option>
                <option value={30}>30 derniers jours</option>
                <option value={90}>90 derniers jours</option>
                <option value={180}>180 derniers jours</option>
                <option value={365}>1 an</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={async () => {
              setSchedBusy(true); setSchedMsg("");
              try {
                if (!schedule?.enabled) {
                  await api.delete("/admin/analytics/email-schedule", headers);
                  setSchedMsg("✓ Envoi automatique désactivé.");
                } else {
                  const { data } = await api.post("/admin/analytics/email-schedule", schedule, headers);
                  setSchedule(data.schedule);
                  setSchedMsg("✓ Configuration enregistrée.");
                }
              } catch (e) {
                setSchedMsg("⚠️ " + (e.response?.data?.detail || "Erreur d'enregistrement"));
              } finally { setSchedBusy(false); }
            }}
            disabled={schedBusy}
            className="btn-primary px-3 py-1.5 rounded-lg text-xs disabled:opacity-40"
            data-testid="analytics-email-save">
            {schedBusy ? "Enregistrement…" : "💾 Enregistrer"}
          </button>
          {schedule?.enabled && schedule?.email && (
            <button
              onClick={async () => {
                setSchedBusy(true); setSchedMsg("");
                try {
                  const { data } = await api.post("/admin/analytics/email-schedule/send-now", {}, headers);
                  setSchedMsg(`✓ Envoyé à ${data.sent_to}.`);
                } catch (e) {
                  setSchedMsg("⚠️ " + (e.response?.data?.detail || "Erreur d'envoi"));
                } finally { setSchedBusy(false); }
              }}
              disabled={schedBusy}
              className="btn-ghost px-3 py-1.5 rounded-lg text-xs disabled:opacity-40"
              data-testid="analytics-email-test">
              📨 Envoyer un test maintenant
            </button>
          )}
          {schedMsg && <span className="text-[11px] text-zinc-300">{schedMsg}</span>}
        </div>
      </div>

      {/* Historique des rapports */}
      {reports.length > 0 && (
        <div className="mb-6">
          <div className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Historique des rapports</div>
          <div className="flex gap-2 flex-wrap">
            {reports.map(r => (
              <button key={r.id} onClick={() => loadReport(r.id)}
                      className="px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 text-xs font-mono">
                {r.generated_at?.slice(0,16)} · {r.total_trades}t · WR {r.winrate ?? "—"}%{r.account ? ` · ${r.account === "unknown" ? "compte inconnu" : "n°" + r.account}` : ""}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Rapport courant */}
      {current && (
        <div className="glass p-6 rounded-xl" data-testid="analytics-report">
          <div className="mb-4 pb-4 border-b border-white/10">
            <div className="text-xs text-zinc-400">Résumé</div>
            <div className="text-lg mt-1">{current.summary}</div>
          </div>

          <AnalyticsAccountsTable accounts={current.by_account} />
          <AnalyticsStrategyReport strategies={current.by_strategy} />

          {current.deep_loss_analysis && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-red-300 mb-2">🔴 Focus deep losses</h3>
              <div className="text-xs text-zinc-300 space-y-1">
                <div>Nombre : <span className="font-mono">{current.deep_loss_analysis.count}</span> — DD moyen : <span className="font-mono">{current.deep_loss_analysis.avg_max_dd}$</span></div>
                <div>Concentration heure : <span className="font-mono">{current.deep_loss_analysis.concentration.hour_utc.value}h UTC ({current.deep_loss_analysis.concentration.hour_utc.share})</span></div>
                <div>Concentration symbole : <span className="font-mono">{current.deep_loss_analysis.concentration.symbol.value} ({current.deep_loss_analysis.concentration.symbol.share})</span></div>
                <div>Concentration stratégie : <span className="font-mono">{current.deep_loss_analysis.concentration.strategy.value} ({current.deep_loss_analysis.concentration.strategy.share})</span></div>
              </div>
            </div>
          )}

          {current.patterns?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-amber-300 mb-2">📌 Patterns anti-productifs (winrate {'<'} 40%)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-zinc-400 uppercase tracking-wider">
                    <tr><th className="text-left py-2">Dimension</th><th className="text-left">Clé</th><th className="text-right">Trades</th><th className="text-right">Winrate</th><th className="text-right">Deep</th><th className="text-right">P&L net</th></tr>
                  </thead>
                  <tbody className="font-mono">
                    {current.patterns.map((p, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="py-1.5">{p.dimension}</td>
                        <td>{p.key}</td>
                        <td className="text-right">{p.trades}</td>
                        <td className={`text-right ${p.winrate<25?"text-red-400":p.winrate<33?"text-orange-400":"text-amber-300"}`}>{p.winrate}%</td>
                        <td className="text-right">{p.deep_losses}</td>
                        <td className={`text-right ${p.net_pnl<0?"text-red-400":"text-emerald-400"}`}>{p.net_pnl>0?"+":""}{p.net_pnl}$</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {current.recommendations?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-emerald-300 mb-2">💡 Recommandations</h3>
              <ul className="space-y-2 text-sm">
                {current.recommendations.map((r, i) => (
                  <li key={i} className="p-3 rounded-lg bg-white/[0.03] border border-white/5">{r.text}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <IdleLogoutWarning
        open={showWarning}
        remainingMs={remaining}
        onStay={stayActive}
        onLogoutNow={doLogout}
      />
    </div>
  );
}
